import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
// probeHealth lives in networkState.js so the axios interceptor (api.js) can
// confirm a suspected outage the same way this provider confirms every
// positive signal — one probe, one host resolution, for both.
import { getOnline, setOnline, subscribe, probeHealth } from "./networkState";

// Same Provider+hook convention as lib/auth.jsx (createContext(null), a
// named XProvider({children}), an exported useX() hook).
const NetworkCtx = createContext(null);

const PROBE_TIMEOUT_MS = 5000;
const OFFLINE_POLL_MS = 5000;

export function NetworkProvider({ children }) {
  const [online, setOnlineState] = useState(getOnline());
  const [checking, setChecking] = useState(true);
  const [reconnectCount, setReconnectCount] = useState(0);
  const wasOnline = useRef(online);
  const pollRef = useRef(null);

  // Local render state mirrors the shared store — the axios interceptors
  // (lib/api.js) also write to this store when a request fails due to a
  // real network error, giving faster-than-polling detection.
  useEffect(() => subscribe((v) => setOnlineState(v)), []);

  // Only count an actual false→true transition as a "reconnect" — not the
  // initial mount, even if we start out online.
  useEffect(() => {
    if (online && !wasOnline.current) setReconnectCount((c) => c + 1);
    wasOnline.current = online;
  }, [online]);

  // Keep re-probing while offline; stop once confirmed back online.
  useEffect(() => {
    if (online) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      if (await probeHealth()) setOnline(true);
    }, OFFLINE_POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [online]);

  // Initial determination (the "check before any API calls" gate) + platform
  // listeners for subsequent changes.
  useEffect(() => {
    let cancelled = false;

    async function confirmAndSet(candidateOnline) {
      if (!candidateOnline) { setOnline(false); return; }
      const ok = await probeHealth();
      if (!cancelled) setOnline(ok);
    }

    function webFallback() {
      // Standard browser APIs — work inside a native WebView too, so this
      // doubles as the safety net below when the native Network plugin
      // isn't available.
      confirmAndSet(navigator.onLine).then(() => { if (!cancelled) setChecking(false); });
      const onOnline = () => confirmAndSet(true);
      const onOffline = () => setOnline(false);
      window.addEventListener("online", onOnline);
      window.addEventListener("offline", onOffline);
      return () => {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", onOffline);
      };
    }

    async function init() {
      if (Capacitor.isNativePlatform()) {
        // An already-published app can be running a native binary that
        // predates this plugin being added (it's JS-bridge only — adding it
        // to package.json does nothing for an app already on the Play/App
        // Store until that build is rebuilt and republished). If the native
        // side doesn't implement it, this call can reject OR simply never
        // resolve — either way `checking` must not be left stuck on `true`
        // forever (that's the app-wide "Loading…" screen), so both a
        // try/catch AND a timeout race guard it, falling back to the same
        // browser-standard detection the web build already uses.
        try {
          const { Network } = await import("@capacitor/network");
          const status = await Promise.race([
            Network.getStatus(),
            new Promise((_, reject) => setTimeout(() => reject(new Error("Network.getStatus timed out")), PROBE_TIMEOUT_MS)),
          ]);
          await confirmAndSet(status.connected);
          if (!cancelled) setChecking(false);
          const handle = await Network.addListener("networkStatusChange", (s) => {
            if (s.connected) confirmAndSet(true);
            else setOnline(false);
          });
          return () => handle.remove();
        } catch {
          return webFallback();
        }
      }
      return webFallback();
    }

    let cleanup;
    init().then((fn) => { cleanup = fn; });
    return () => { cancelled = true; if (cleanup) cleanup(); };
  }, []);

  // Exposed for a manual "Try again" button — the offline poll loop already
  // retries automatically every OFFLINE_POLL_MS, this just lets the user
  // force an immediate attempt instead of waiting for the next tick.
  const retry = async () => {
    if (await probeHealth()) setOnline(true);
  };

  return (
    <NetworkCtx.Provider value={{ online, checking, reconnectCount, retry }}>
      {children}
    </NetworkCtx.Provider>
  );
}

export const useNetwork = () => useContext(NetworkCtx);
